import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { Shell } from '../components/chrome/shell'
import { CommandPalette } from '../components/chrome/palette'
import {
  api,
  type ContactPage,
  type Domain,
  type SuppressionEntry,
} from '../lib/api'
import { useSession } from '../lib/session'
import { AuditScreen } from '../screens/audit'
import { CampaignsScreen } from '../screens/campaigns'
import { ContactsScreen } from '../screens/contacts'
import { DeliverabilityScreen } from '../screens/deliverability'
import { DomainsScreen } from '../screens/domains'
import { LoginScreen } from '../screens/login'
import { MailboxesScreen } from '../screens/mailboxes'
import { OverviewScreen } from '../screens/overview'
import { PlatformScreen } from '../screens/platform'
import { WebhooksScreen } from '../screens/webhooks'
import { RepliesScreen } from '../screens/replies'
import { SchedulesScreen } from '../screens/schedules'
import { SettingsScreen } from '../screens/settings'
import { SuppressionsScreen } from '../screens/suppressions'
import { ObservabilityScreen } from '../screens/observability'
import { AlertsScreen } from '../screens/alerts'
import { WarmupScreen } from '../screens/warmup'
import { TemplatesScreen } from '../screens/templates'
import { UnwiredScreen } from '../screens/unwired'
import { NAV, type Route } from './nav'

/**
 * Which routes have a screen of their own. Derived from `nav.ts` rather than written out twice —
 * a route marked `wired` with no component here would fall through to the unwired screen and
 * silently claim its endpoint does not exist, which is the one lie this console is gated against.
 */
const WIRED = new Set<Route>(NAV.filter((entry) => entry.wired).map((entry) => entry.id))

/**
 * The route the URL asks for, if it names one.
 *
 * Routing is otherwise local state — see below — but **one flow arrives from outside this app and
 * has to land somewhere specific**: Google's OAuth callback redirects the browser to
 * `/mailboxes?connect=…` after the operator consents. Without this, that redirect served
 * `index.html`, the app started on Overview, and somebody who had just authorised their mail
 * account saw a dashboard with no acknowledgement that anything had happened.
 *
 * Found by G022's browser gate on 2026-08-21, which is the kind of thing only a browser can find:
 * every API assertion passed, and the redirect was correct. It just went nowhere.
 *
 * Deliberately one-way and one-time. This reads the entry URL and nothing else — it does not push
 * on navigation, does not listen to `popstate`, and does not make anything shareable. It is the
 * smallest thing that makes an external redirect land, not a router pretending to be one.
 */
function routeFromUrl(): Route | null {
  const slug = window.location.pathname.replace(/^\/+|\/+$/g, '')
  if (!slug) return null
  const match = NAV.find((entry) => entry.id === slug)
  return match ? match.id : null
}

/**
 * Routing is local state.
 *
 * TanStack Router is installed and is the decided choice (P017), but nothing is deep-linkable
 * yet — the domain detail is a selection inside its screen rather than a URL. It goes in when the
 * first list needs a shareable filtered view. Until then a router is ceremony around a `useState`.
 */
export function App() {
  const { user, loading, can } = useSession()
  const [route, setRoute] = useState<Route>(() => routeFromUrl() ?? 'overview')
  const [crumb, setCrumb] = useState<string | undefined>(undefined)
  const [palette, setPalette] = useState(false)

  const navigate = useCallback((next: Route) => {
    setRoute(next)
    setCrumb(undefined)
  }, [])

  // `/` opens the palette, the way every console the owner referenced does. Ignored while typing,
  // because a search shortcut that hijacks a text field is worse than no shortcut.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable
      if (event.key === '/' && !typing) {
        event.preventDefault()
        setPalette(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Sidebar counts, and only real ones. An entry with no endpoint shows no number rather than a
  // placeholder — see `nav.ts`.
  const contacts = useQuery({
    queryKey: ['nav-count', 'contacts'],
    queryFn: () => api.get<ContactPage>('/contacts?take=1'),
    enabled: Boolean(user) && can('view_contacts'),
  })
  const domains = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.get<Domain[]>('/domains'),
    enabled: Boolean(user) && can('manage_domains'),
  })
  const suppressions = useQuery({
    queryKey: ['suppression'],
    queryFn: () => api.get<SuppressionEntry[]>('/suppression'),
    enabled: Boolean(user) && can('manage_suppression'),
  })

  if (loading) {
    return (
      <div className="bg-bg text-faint flex h-full items-center justify-center text-[12.5px]">
        Loading…
      </div>
    )
  }

  if (!user) return <LoginScreen />

  const counts: Partial<Record<Route, number>> = {
    contacts: contacts.data?.total,
    domains: domains.data?.length,
    suppressions: suppressions.data?.length,
  }

  return (
    <>
      <Shell
        route={route}
        crumb={crumb}
        counts={counts}
        onNavigate={navigate}
        onOpenPalette={() => setPalette(true)}
      >
        {route === 'overview' && <OverviewScreen onNavigate={navigate} />}
        {route === 'mailboxes' && <MailboxesScreen />}
        {route === 'replies' && <RepliesScreen />}
        {route === 'campaigns' && <CampaignsScreen onCrumb={setCrumb} />}
        {route === 'contacts' && <ContactsScreen />}
        {route === 'domains' && <DomainsScreen onCrumb={setCrumb} />}
        {route === 'templates' && <TemplatesScreen />}
        {route === 'warmup' && <WarmupScreen />}
        {route === 'deliverability' && <DeliverabilityScreen />}
        {route === 'schedules' && <SchedulesScreen />}
        {route === 'suppressions' && <SuppressionsScreen />}
        {route === 'observability' && <ObservabilityScreen />}
        {route === 'alerts' && <AlertsScreen />}
        {route === 'audit' && <AuditScreen />}
        {route === 'webhooks' && <WebhooksScreen />}
        {route === 'platform' && <PlatformScreen />}
        {route === 'settings' && <SettingsScreen />}
        {!WIRED.has(route) && <UnwiredScreen route={route} />}
      </Shell>

      <CommandPalette
        open={palette}
        onClose={() => setPalette(false)}
        onNavigate={(next) => {
          navigate(next)
          setPalette(false)
        }}
      />
    </>
  )
}
